import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@resideo/blueprint-oneportal';
import { SectionHeading, Desc, Divider, Control, StyledSelect } from '../shared/primitives';
import styles from './RecordingSection.module.css';

interface RecordingSectionProps {
  onEventRecordingChange?: (value: string) => void;
  onContinuousRecordingChange?: (value: string) => void;
}

const RecordingSection: React.FC<RecordingSectionProps> = ({ onEventRecordingChange, onContinuousRecordingChange }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const EVENT_RECORDING_OPTIONS = [
    t('bulkEditSettings.recording.defaultEventBehavior.options.automatic'),
    t('common.disabled'),
  ];
  const CONTINUOUS_RECORDING_OPTIONS = [
    t('common.enabled'),
    t('common.disabled'),
  ];
  const [eventRecording, setEventRecording] = useState(EVENT_RECORDING_OPTIONS[0]);
  const [continuousRecording, setContinuousRecording] = useState(CONTINUOUS_RECORDING_OPTIONS[0]);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleContinue = () => {
    setShowConfirm(false);
    const cameras = searchParams.get('cameras') ?? '';
    navigate(`/camera/bulk-edit/replace-schedule?cameras=${encodeURIComponent(cameras)}`);
  };

  return (
    <>
      {showConfirm && (
        <div className={styles.overlay} onClick={() => setShowConfirm(false)}>
          <div className={styles.modalBox} onClick={e => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>{t('bulkEditSettings.recording.modal.title')}</h3>
            <p className={styles.modalBody}>{t('bulkEditSettings.recording.modal.body')}</p>
            <div className={styles.modalFooter}>
              <Button variant="secondary" size="small" onClick={() => setShowConfirm(false)}>
                {t('common.cancel')}
              </Button>
              <Button variant="primary" size="small" onClick={handleContinue}>
                {t('common.continue')}
              </Button>
            </div>
          </div>
        </div>
      )}

      <SectionHeading>{t('bulkEditSettings.recording.defaultRecordingBehavior.heading')}</SectionHeading>
      <Desc>{t('bulkEditSettings.recording.defaultRecordingBehavior.desc')}</Desc>

      <div className={styles.behaviorBlock}>
        <div className={styles.behaviorRow}>
          <div className={styles.behaviorLabel}><div className={styles.behaviorLabelTitle}>{t('bulkEditSettings.recording.defaultEventBehavior.heading')}</div></div>
          <Control>
            <StyledSelect name="eventRecording" value={eventRecording} onChange={e => { setEventRecording(e.target.value); onEventRecordingChange?.(e.target.value); }}>
              {EVENT_RECORDING_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </StyledSelect>
          </Control>
        </div>
        <div className={styles.behaviorNotes}>
          <p className={styles.noteP}>{t('bulkEditSettings.recording.defaultEventBehavior.note1')}</p>
          <p className={styles.noteP}>{t('bulkEditSettings.recording.defaultEventBehavior.note2')}</p>
        </div>
      </div>

      <div className={styles.behaviorBlock}>
        <div className={styles.behaviorRow}>
          <div className={styles.behaviorLabel}><div className={styles.behaviorLabelTitle}>{t('bulkEditSettings.recording.defaultContinuousBehavior.heading')}</div></div>
          <Control>
            <StyledSelect name="continuousRecording" value={continuousRecording} onChange={e => { setContinuousRecording(e.target.value); onContinuousRecordingChange?.(e.target.value); }}>
              {CONTINUOUS_RECORDING_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </StyledSelect>
          </Control>
        </div>
        <div className={styles.behaviorNotes}>
          <p className={styles.noteP}>{t('bulkEditSettings.recording.defaultContinuousBehavior.note0')}</p>
          <p className={styles.noteP}>{t('bulkEditSettings.recording.defaultContinuousBehavior.note1')}</p>
          <p className={styles.noteP}>{t('bulkEditSettings.recording.defaultContinuousBehavior.note2')}</p>
        </div>
      </div>

      <Divider />

      <div className={styles.scheduleRow}>
        <div>
          <SectionHeading>{t('bulkEditSettings.recording.schedule.heading')}</SectionHeading>
          <Desc>{t('bulkEditSettings.recording.schedule.desc')}</Desc>
        </div>
        <Button variant="secondary" size="small" onClick={() => setShowConfirm(true)}>
          {t('bulkEditSettings.recording.schedule.replaceBtn')}
        </Button>
      </div>
    </>
  );
};

export default RecordingSection;
